import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { showToast } from "../../../toast";
import { AvatarImage } from "../../components/AvatarImage";
import { firstNameOf } from "../home/CoupleHeader";
import { ReactionBurst } from "../together/ReactionBurst";
import {
  setWheelSocketHandlers,
  onSocketOpen,
  type WheelDeclinedData,
  type WheelEndedData,
  type WheelInviteData,
  type WheelJoinedData,
  type WheelReactionData,
  type WheelResultData,
} from "../../../chatSocket";
import {
  acceptWheelApi,
  applyWheelDeclined,
  applyWheelEnded,
  applyWheelInvite,
  applyWheelJoined,
  applyWheelReaction,
  applyWheelResult,
  declineWheelApi,
  fetchWheelCategoriesApi,
  fetchWheelSessionApi,
  inviteWheelApi,
  leaveWheelApi,
  reactWheelApi,
  spinWheelApi,
  toggleWheelFavoriteApi,
} from "../../../wheelApi";
import type { WheelCategoryMeta } from "../../../types";
import { WheelCanvas } from "./WheelCanvas";

const REACTION_EMOJIS = [
  "❤️",
  "\u{1F525}",
  "\u{1F60D}",
  "\u{1F602}",
  "\u{1F62E}",
  "\u{1F44F}",
];

function IntensityDots({ intensity }: { intensity: number }) {
  return (
    <span
      className="wheel-result-intensity"
      aria-label={`Intensity ${intensity} of 3`}
    >
      {[1, 2, 3].map((n) => (
        <i
          key={n}
          className={`bi ${n <= intensity ? "bi-heart-fill" : "bi-heart"}`}
          style={{ color: "var(--pink-500)", fontSize: "0.85rem" }}
          aria-hidden="true"
        />
      ))}
    </span>
  );
}

// Full route page (/couple-wheel), portaled into #coupleWheelView - unlike
// GameRoot/TogetherRoot (session-driven overlays portaled to document.body),
// this feature was explicitly asked to be a real page, so it follows
// ProfileScreen.tsx's container-portal shape instead: WS handler
// registration, reconnect-resync, and session-driven rendering exactly like
// GameRoot.tsx's, just rendered inline in the page flow rather than as a
// floating dialog stack. The one exception is the incoming-invite banner
// (see inviteBanner below) - that still needs to reach the recipient
// wherever they currently are, not just while they happen to already be on
// this route, so it gets its own separate document.body portal, same as
// Together/Game/Party's own invite popups.
export function CoupleWheelPage() {
  const navigate = useNavigate();
  const wheelSession = useAppState((s) => s.wheelSession);
  const wheelIncomingInvite = useAppState((s) => s.wheelIncomingInvite);
  const wheelReactions = useAppState((s) => s.wheelReactions);
  const myEmail = useAppState((s) => s.currentUserEmail);
  const myFullName = useAppState((s) => s.currentUserFullName);
  const myAvatarFilename = useAppState((s) => s.currentUserAvatarFilename);
  const presenceUsers = useAppState((s) => s.presenceUsers);

  const [categories, setCategories] = useState<WheelCategoryMeta[] | null>(
    null,
  );
  const [inviting, setInviting] = useState(false);
  const [respondingToInvite, setRespondingToInvite] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [favoriting, setFavoriting] = useState(false);
  // Real per-position artwork may not exist yet (coupleWheelData.js's
  // imageUrl is a convention, not a guarantee - see its own comment) - a
  // failed load falls back to the icon instead of showing a broken image.
  // Keyed off the result id so a new spin always gets a fresh attempt even
  // if a previous, different position's image failed.
  const [failedImageId, setFailedImageId] = useState<string | null>(null);

  useEffect(() => {
    fetchWheelCategoriesApi().then(setCategories);
  }, []);

  useEffect(() => {
    setWheelSocketHandlers({
      onWheelInvite: (data: WheelInviteData) => applyWheelInvite(data),
      onWheelJoined: (data: WheelJoinedData) => applyWheelJoined(data),
      onWheelDeclined: (data: WheelDeclinedData) => applyWheelDeclined(data),
      onWheelResult: (data: WheelResultData) => applyWheelResult(data),
      onWheelReaction: (data: WheelReactionData) => applyWheelReaction(data),
      onWheelEnded: (data: WheelEndedData) => applyWheelEnded(data),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // wheel:* events are push-only over the WS - anything that happened while
  // this socket was down is otherwise silently missed. Deliberately
  // unconditional (no "only if we already have local state" guard) -
  // onSocketOpen fires on the very first connect too, same reasoning as
  // GameRoot.tsx's identical effect.
  useEffect(() => {
    return onSocketOpen(() => {
      const hadSession = !!appState.wheelSession;
      const hadInvite = !!appState.wheelIncomingInvite;
      fetchWheelSessionApi().then((fresh) => {
        if (!fresh) {
          if (hadSession)
            showToast(
              "Your Couple Wheel session ended while you were disconnected.",
              "info",
            );
          if (hadSession || hadInvite) {
            appState.wheelSession = null;
            appState.wheelIncomingInvite = null;
            notifyStateChange();
          }
          return;
        }

        if (fresh.status === "pending" && !fresh.isHost) {
          appState.wheelSession = null;
          appState.wheelIncomingInvite = {
            sessionId: fresh.id,
            fromUserId: fresh.peerUserId,
            fromName: fresh.peerName,
            category: fresh.category,
            conversationId: fresh.conversationId,
          };
        } else {
          appState.wheelSession = fresh;
          appState.wheelIncomingInvite = null;
        }
        notifyStateChange();
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleInvite(): Promise<void> {
    setInviting(true);
    const result = await inviteWheelApi({});
    setInviting(false);
    if (!result.ok)
      showToast(result.error || "Failed to send invite.", "error");
  }

  async function handleAccept(): Promise<void> {
    if (!wheelIncomingInvite) return;
    setRespondingToInvite(true);
    const result = await acceptWheelApi(wheelIncomingInvite.sessionId);
    setRespondingToInvite(false);
    if (!result.ok) showToast(result.error || "Failed to join.", "error");
  }

  async function handleDecline(): Promise<void> {
    if (!wheelIncomingInvite) return;
    setRespondingToInvite(true);
    await declineWheelApi(wheelIncomingInvite.sessionId);
    setRespondingToInvite(false);
  }

  async function handleLeave(): Promise<void> {
    if (!wheelSession) return;
    setLeaving(true);
    await leaveWheelApi(wheelSession.id);
    setLeaving(false);
  }

  async function handleSpin(): Promise<void> {
    if (!wheelSession) return;
    setSpinning(true);
    const result = await spinWheelApi(wheelSession.id);
    setSpinning(false);
    if (!result.ok && result.error !== "spin_in_progress") {
      showToast(result.error || "Failed to spin.", "error");
    }
  }

  async function handleToggleFavorite(): Promise<void> {
    if (!wheelSession?.currentResultId) return;
    setFavoriting(true);
    await toggleWheelFavoriteApi(wheelSession.id, wheelSession.currentResultId);
    setFavoriting(false);
  }

  async function handleReact(emoji: string): Promise<void> {
    if (!wheelSession) return;
    await reactWheelApi(wheelSession.id, emoji);
    appState.wheelReactions = [
      ...appState.wheelReactions,
      { id: `me-${Date.now()}`, emoji, fromUserId: myEmail },
    ];
    notifyStateChange();
  }

  function removeReaction(id: string): void {
    appState.wheelReactions = appState.wheelReactions.filter(
      (r) => r.id !== id,
    );
    notifyStateChange();
  }

  // The incoming-invite banner needs to be visible from ANY screen (Home,
  // Chat, wherever the recipient happens to be) - same expectation Together/
  // Game/Party's own invite popups already meet by portaling to
  // document.body. Everything else about this page stays exactly as
  // designed (a real route, portaled into #coupleWheelView, only visible
  // while /couple-wheel is the active screen) - only this one piece needs
  // its own always-visible portal, decoupled from that route gate.
  const inviteBanner = wheelIncomingInvite && (
    <div
      className="glass p-3"
      style={{
        position: "fixed",
        bottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1400,
        minWidth: 280,
        maxWidth: "90vw",
      }}
    >
      <div className="d-flex align-items-center gap-2 mb-2">
        <i
          className="bi bi-suit-heart-fill text-pink"
          style={{ fontSize: "1.3rem" }}
          aria-hidden="true"
        />
        <span className="fw-semibold">
          {wheelIncomingInvite.fromName} wants to spin the Couple Wheel
        </span>
      </div>
      <div className="d-flex gap-2 justify-content-end">
        <Button
          size="small"
          onClick={handleDecline}
          disabled={respondingToInvite}
        >
          Decline
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={handleAccept}
          disabled={respondingToInvite}
        >
          Spin
        </Button>
      </div>
    </div>
  );

  const container = getVanillaEl<HTMLDivElement>("coupleWheelView");
  if (!container)
    return inviteBanner ? createPortal(inviteBanner, document.body) : null;

  const items = categories?.[0]?.items ?? [];
  const resultItem = wheelSession?.currentResultId
    ? items.find((it) => it.id === wheelSession.currentResultId)
    : null;
  const isFavorite = !!(
    wheelSession?.currentResultId &&
    wheelSession.favorites.includes(wheelSession.currentResultId)
  );
  const partner =
    presenceUsers.find((u) => u.id === wheelSession?.peerUserId) ??
    presenceUsers[0] ??
    null;
  const myDisplayName = firstNameOf(myFullName, myEmail);
  const peerDisplayName = wheelSession
    ? wheelSession.peerName
    : partner
      ? firstNameOf(partner.fullName, partner.email)
      : "";

  return (
    <>
      {inviteBanner && createPortal(inviteBanner, document.body)}
      {createPortal(
        <>
          <div className="profile-page-header">
            <button
              type="button"
              className="btn btn-outline-pink btn-sm"
              aria-label="Back"
              onClick={() => navigate(-1)}
            >
              <i className="bi bi-arrow-left" aria-hidden="true" />
            </button>
            <span className="profile-page-header-title">Couple Wheel</span>
            {wheelSession && (
              <button
                type="button"
                className="btn btn-outline-pink btn-sm ms-auto"
                onClick={handleLeave}
                disabled={leaving}
                aria-label="Leave"
              >
                {leaving ? "…" : "Leave"}
              </button>
            )}
          </div>

          <div className="profile-page-content">
            <div className="wheel-couple-row">
              <div className="wheel-couple-avatar">
                {myAvatarFilename ? (
                  <AvatarImage
                    filename={myAvatarFilename}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    fallback={<i className="bi bi-person-fill" />}
                  />
                ) : (
                  <i className="bi bi-person-fill" aria-hidden="true" />
                )}
              </div>
              <i
                className="bi bi-heart-fill wheel-couple-connector"
                aria-hidden="true"
              />
              <div className="wheel-couple-avatar">
                {(wheelSession ? true : !!partner) ? (
                  partner?.avatarFilename ? (
                    <AvatarImage
                      filename={partner.avatarFilename}
                      alt=""
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      fallback={<i className="bi bi-person-fill" />}
                    />
                  ) : (
                    <i className="bi bi-person-fill" aria-hidden="true" />
                  )
                ) : (
                  <i className="bi bi-person" aria-hidden="true" />
                )}
              </div>
            </div>
            <div className="text-center small muted mb-3">
              {myDisplayName}
              {peerDisplayName ? <> &amp; {peerDisplayName}</> : ""}
            </div>

            {!wheelSession && !wheelIncomingInvite && (
              <div className="text-center py-4">
                <div style={{ fontSize: "2.5rem" }} aria-hidden="true">
                  <i className="bi bi-suit-heart-fill text-pink" />
                </div>
                <div className="fw-semibold mb-1">Spin together</div>
                <div className="small muted mb-3">
                  Invite {peerDisplayName || "your partner"} to spin the wheel
                  and land on the same result, together.
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleInvite}
                  disabled={inviting || !partner}
                >
                  {inviting ? "Inviting…" : "Invite to spin"}
                </Button>
                {!partner && (
                  <div className="small muted mt-2">
                    Waiting for your partner to appear in the app.
                  </div>
                )}
              </div>
            )}

            {wheelSession?.status === "pending" && (
              <div className="text-center py-4">
                <div
                  className="spinner-border text-pink mb-2"
                  role="status"
                  style={{ width: 32, height: 32 }}
                />
                <div className="small muted">
                  Waiting for {wheelSession.peerName} to join…
                </div>
              </div>
            )}

            {wheelSession?.status === "active" && (
              <>
                <div className="wheel-canvas-wrap">
                  <WheelCanvas
                    items={items}
                    resultId={wheelSession.currentResultId}
                    spinNumber={wheelSession.spinNumber}
                    disabled={spinning}
                    onSpin={handleSpin}
                  />
                </div>

                {wheelSession.spinState === "result" && resultItem && (
                  <div className="wheel-result-card">
                    {failedImageId === resultItem.id ? (
                      <div className="wheel-result-icon">
                        <i
                          className={`bi ${resultItem.icon}`}
                          aria-hidden="true"
                        />
                      </div>
                    ) : (
                      <img
                        key={resultItem.id}
                        src={resultItem.imageUrl}
                        alt=""
                        className="wheel-result-photo"
                        onError={() => setFailedImageId(resultItem.id)}
                      />
                    )}
                    <div className="wheel-result-name">{resultItem.name}</div>
                    <IntensityDots intensity={resultItem.intensity} />
                    <div className="wheel-result-actions">
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleSpin}
                        disabled={spinning}
                      >
                        {spinning ? "Spinning…" : "Spin Again"}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleToggleFavorite}
                        disabled={favoriting}
                        startIcon={
                          <i
                            className={`bi ${isFavorite ? "bi-heart-fill" : "bi-heart"}`}
                            aria-hidden="true"
                          />
                        }
                      >
                        {isFavorite ? "Favorited" : "Favorite"}
                      </Button>
                    </div>
                    <div className="wheel-reaction-row">
                      {REACTION_EMOJIS.map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          className="wheel-reaction-btn"
                          onClick={() => handleReact(emoji)}
                          aria-label={`React ${emoji}`}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {wheelSession.history.length > 0 && (
                  <>
                    <div className="wheel-section-label">Recent spins</div>
                    <div className="wheel-recent-list">
                      {wheelSession.history.map((resultId, i) => {
                        const item = items.find((it) => it.id === resultId);
                        if (!item) return null;
                        const isFav = wheelSession.favorites.includes(resultId);
                        return (
                          <span
                            key={`${resultId}-${i}`}
                            className={`wheel-recent-chip${isFav ? " is-favorite" : ""}`}
                          >
                            <i
                              className={`bi ${item.icon}`}
                              aria-hidden="true"
                            />
                            {item.name}
                          </span>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {wheelReactions.map((r) => (
            <ReactionBurst
              key={r.id}
              emoji={r.emoji}
              onDone={() => removeReaction(r.id)}
            />
          ))}
        </>,
        container,
      )}
    </>
  );
}
